import React from "react";
import { Badge, Box, SpaceBetween } from "@amzn/awsui-components-react";
import { TableProps } from "@amzn/awsui-components-react/polaris/table";
import { PropertyFilterProperty } from "@amzn/awsui-collection-hooks";
import { TestedPartyMapping, VersionedTestedPartyMapping } from "src/models/tested-party-mapping/TestedPartyMapping";

export const CONFIG_TYPE_TESTED_PARTY = "TESTED_PARTY";
export const LATEST_VERSION_KEY = "v0";
export const OPERATION_UPDATE = "UPDATE";

export const sourceBadge = (source: string) =>
    source === "SYSTEM_DEFAULT"
        ? <Badge color="grey">Defaulted</Badge>
        : <Badge color="green">User Assigned</Badge>;

export const COLUMN_LABEL: Record<string, string> = {
    cliNumber: "CLI Number",
    agreementNumber: "Agreement Number",
    agreementName: "Agreement Name",
    firstPartyEntityName: "First Party",
    firstPartyCompanyCode: "First Party Code",
    secondPartyEntityName: "Second Party",
    secondPartyCompanyCode: "Second Party Code",
    testedPartyEntityName: "Tested Party",
    testedPartyCompanyCode: "Tested Party Code",
    testedPartyCurrency: "Currency",
    source: "Source",
    updatedBy: "Updated By",
    updatedAt: "Updated At",
};

export const PAGE_SIZE_OPTIONS = [
    { value: 10, label: "10 items" },
    { value: 25, label: "25 items" },
    { value: 50, label: "50 items" },
];

export const DEFAULT_PREFERENCES = {
    pageSize: 10,
    contentDisplay: [
        { id: "cliNumber", visible: true },
        { id: "agreementNumber", visible: true },
        { id: "firstPartyEntityName", visible: true },
        { id: "secondPartyEntityName", visible: true },
        { id: "testedPartyEntityName", visible: true },
        { id: "testedPartyCompanyCode", visible: true },
        { id: "source", visible: true },
        { id: "updatedBy", visible: true },
        { id: "updatedAt", visible: true },
    ],
};

export const VISIBLE_CONTENT_OPTIONS = DEFAULT_PREFERENCES.contentDisplay.map((c) => ({
    id: c.id,
    label: COLUMN_LABEL[c.id],
    alwaysVisible: c.id === "cliNumber",
}));

export const mappingsColumnDefinitions: TableProps.ColumnDefinition<TestedPartyMapping>[] = [
    { id: "cliNumber", header: COLUMN_LABEL.cliNumber, cell: (e) => e.cliNumber, sortingField: "cliNumber", isRowHeader: true },
    { id: "agreementNumber", header: COLUMN_LABEL.agreementNumber, cell: (e) => e.agreementNumber, sortingField: "agreementNumber" },
    { id: "agreementName", header: COLUMN_LABEL.agreementName, cell: (e) => e.agreementName, sortingField: "agreementName" },
    { id: "firstPartyEntityName", header: COLUMN_LABEL.firstPartyEntityName, cell: (e) => `${e.firstPartyEntityName} (${e.firstPartyCompanyCode})`, sortingField: "firstPartyEntityName" },
    { id: "firstPartyCompanyCode", header: COLUMN_LABEL.firstPartyCompanyCode, cell: (e) => e.firstPartyCompanyCode, sortingField: "firstPartyCompanyCode" },
    { id: "secondPartyEntityName", header: COLUMN_LABEL.secondPartyEntityName, cell: (e) => `${e.secondPartyEntityName} (${e.secondPartyCompanyCode})`, sortingField: "secondPartyEntityName" },
    { id: "secondPartyCompanyCode", header: COLUMN_LABEL.secondPartyCompanyCode, cell: (e) => e.secondPartyCompanyCode, sortingField: "secondPartyCompanyCode" },
    { id: "testedPartyEntityName", header: COLUMN_LABEL.testedPartyEntityName, cell: (e) => e.testedPartyEntityName, sortingField: "testedPartyEntityName" },
    { id: "testedPartyCompanyCode", header: COLUMN_LABEL.testedPartyCompanyCode, cell: (e) => e.testedPartyCompanyCode, sortingField: "testedPartyCompanyCode" },
    { id: "testedPartyCurrency", header: COLUMN_LABEL.testedPartyCurrency, cell: (e) => e.testedPartyCurrency, sortingField: "testedPartyCurrency" },
    {
        id: "source",
        header: COLUMN_LABEL.source,
        cell: (e) => sourceBadge(e.source),
        sortingField: "source",
    },
    { id: "updatedBy", header: COLUMN_LABEL.updatedBy, cell: (e) => e.updatedBy, sortingField: "updatedBy" },
    { id: "updatedAt", header: COLUMN_LABEL.updatedAt, cell: (e) => new Date(e.updatedAt).toLocaleString(), sortingField: "updatedAt" },
];

export const filteringProperties: PropertyFilterProperty[] = [
    { key: "cliNumber", propertyLabel: COLUMN_LABEL.cliNumber, groupValuesLabel: "CLI values", operators: ["=", "!="] },
    { key: "agreementNumber", propertyLabel: COLUMN_LABEL.agreementNumber, groupValuesLabel: "Agreement numbers", operators: ["=", "!="] },
    { key: "firstPartyCompanyCode", propertyLabel: "First Party Company Code", groupValuesLabel: "First party codes", operators: ["=", "!="] },
    { key: "firstPartyEntityName", propertyLabel: COLUMN_LABEL.firstPartyEntityName, groupValuesLabel: "First party values", operators: ["=", "!="] },
    { key: "secondPartyEntityName", propertyLabel: COLUMN_LABEL.secondPartyEntityName, groupValuesLabel: "Second party values", operators: ["=", "!="] },
    { key: "testedPartyEntityName", propertyLabel: COLUMN_LABEL.testedPartyEntityName, groupValuesLabel: "Tested party values", operators: ["=", "!="] },
    { key: "source", propertyLabel: COLUMN_LABEL.source, groupValuesLabel: "Source values", operators: ["=", "!="] },
];

export const versionColumnDefinitions: TableProps.ColumnDefinition<VersionedTestedPartyMapping>[] = [
    {
        id: "currentVersion",
        header: "Version",
        cell: (e) => (
            <SpaceBetween direction="horizontal" size="xs">
                <span>{e.isLatest ? e.versionKey.replace("latest", "0") : e.versionKey.replace("v", "")}</span>
                {e.isLatest && <Badge color="green">Latest</Badge>}
            </SpaceBetween>
        ),
        sortingField: "currentVersion",
        isRowHeader: true,
    },
    {
        id: "testedParty",
        header: "Tested Party",
        cell: (e) => (
            <div>
                <div>{e.testedPartyEntityName}</div>
                <Box variant="small">Entity ID: {e.testedPartyCompanyCode}</Box>
            </div>
        ),
    },
    {
        id: "source",
        header: "Source",
        cell: (e) => sourceBadge(e.source),
    },
    {
        id: "updatedBy",
        header: "Updated By",
        cell: (e) => e.updatedBy,
    },
    {
        id: "updatedAt",
        header: "Updated At",
        cell: (e) => new Date(e.updatedAt).toLocaleString(),
    },
    {
        id: "comment",
        header: "Comment",
        cell: (e) => e.comment || "-",
    },
];
